import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DicesIcon, FileTextIcon, FolderSearchIcon, LayoutGridIcon, LightbulbIcon, PencilRulerIcon, ShapesIcon, ShuffleIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/3724e2ee-d2eb-4487-b051-d7663ac07b3d/1793844258703_caja-12331979.png';
export const heroGuarantees = ['Descărcare digitală imediată', 'Plată unică', 'Fără abonament', 'Garanție de satisfacție 7 zile'];
export const stats = [{
  value: '240',
  label: 'Pagini în volumele principale'
}, {
  value: '6',
  label: 'Volume digitale de matematică'
}, {
  value: '8',
  label: 'Clase – de la I la a VIII-a'
}, {
  value: '5',
  label: 'Bonusuri incluse'
}, {
  value: '1',
  label: 'Sistem complet de predare'
}];
export const differenceStats = [{
  value: 'Clasele I–VIII',
  label: 'Material organizat pentru primar și gimnaziu'
}, {
  value: '6 volume digitale',
  label: 'De la numere și operații la fracții, geometrie și algebră'
}, {
  value: 'PDF-uri printabile',
  label: 'Le folosești digital sau le printezi pentru clasă'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Duminicile dispar',
  text: 'Ore întregi în fața calculatorului ca să pregătești orele de matematică ale săptămânii viitoare – și tot nu ai terminat.'
}, {
  title: 'Ore de căutat pe internet',
  text: 'Fișele găsite pe diverse site-uri sunt prea simple, prea grele sau au greșeli la răspunsuri și trebuie verificate înainte să le poți folosi.'
}, {
  title: 'Fișele se fac de la zero',
  text: 'Exerciții, desene, așezare în pagină și rezolvări – ce ar trebui să dureze minute devine o muncă lungă.'
}, {
  title: 'Greu de făcut matematica vizuală',
  text: 'Valoarea de poziție, fracțiile sau geometria cer desene clare, axe și tabele pentru fiecare temă nouă.'
}, {
  title: 'Evaluarea durează',
  text: 'Teste, bareme, calificative, note și urmărirea progresului elevilor înseamnă și mai multă muncă după ore.'
}, {
  title: 'Stres înainte de fiecare oră',
  text: 'Când timpul nu ajunge, improvizezi și aduni exerciții în ultimul minut.'
}];
export const afterItems = ['Un pachet organizat de matematică pentru clasele I – a VIII-a', 'Lecții gata de predat, minut cu minut', 'Explicații vizuale: blocuri, axa numerelor, tabele', 'Fișe de lucru care se printează direct', 'Exerciții gradate, de la simplu la provocare', 'Jocuri matematice pentru calculul mintal', 'Ce predai la fiecare clasă, organizat pe volume', 'Teste, bareme și răspunsuri calculate', 'Calificative la primar și note la gimnaziu'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Duminica se duce pe planificare',
  text: 'Îți petreci o parte din weekend pregătind orele de matematică pentru săptămâna viitoare?'
}, {
  icon: FolderSearchIcon,
  title: 'Cauți mereu exerciții potrivite',
  text: 'Cauți fișe cu exerciții pe măsura clasei tale – nici prea ușoare, nici prea grele?'
}, {
  icon: LightbulbIcon,
  title: 'Vrei explicații mai clare',
  text: 'Ai nevoie de material vizual ca să explici simplu valoarea de poziție, fracțiile sau geometria?'
}, {
  icon: PencilRulerIcon,
  title: 'Vrei să nu mai faci totul de la zero',
  text: 'Ți-ai dori ca lecțiile, fișele și testele să fie deja pregătite, cu răspunsurile verificate?'
}, {
  icon: ShuffleIcon,
  title: 'Elevii au nevoie de mai multă implicare',
  text: 'Vrei mai multe jocuri și activități care să-i țină atenți la ora de matematică?'
}, {
  icon: LayoutGridIcon,
  title: 'Ai nevoie de o structură clară',
  text: 'Vrei tot materialul adunat la un loc, ca să găsești repede ce-ți trebuie?'
}];

// Spine colours mirror the physical box (green, blue, orange, red, purple, teal).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volumul 01',
  level: 'Cl. I–VI',
  title: 'Numerele naturale',
  tagline: 'Numere, valoarea de poziție și divizibilitate.',
  intro: 'Primul volum pornește de la numărare și ajunge la puteri, divizibilitate și numere prime, cu lecții minut cu minut.',
  items: ['Numerele 0–20, 0–100 și 0–1 000', 'Zeci, sute, mii – valoarea de poziție', 'Comparare, ordonare, rotunjire', 'Cifrele romane', 'Numerele până la 1 000 000', 'Puterea unui număr natural', 'Criteriile de divizibilitate', 'Numere prime și factori primi', 'Teste cu barem și răspunsuri'],
  outro: 'Baza pe care se construiește toată matematica.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volumul 02',
  level: 'Cl. I–VIII',
  title: 'Adunarea și scăderea',
  tagline: 'Calcul mintal, în scris și probleme.',
  intro: 'Al doilea volum construiește pas cu pas adunarea și scăderea, de la trecerea peste ordin la probleme cu mai multe operații.',
  items: ['Calcul mintal rapid', 'Trecerea peste ordin', 'Adunarea și scăderea în scris', 'Proba operațiilor', 'Probleme cu una și două operații', 'Estimarea rezultatelor', 'Numere întregi la gimnaziu', 'Fișe cu răspunsuri', 'Teste scurte'],
  outro: 'Calcul sigur și rapid, la fiecare clasă.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volumul 03',
  level: 'Cl. II–VIII',
  title: 'Înmulțirea și împărțirea',
  tagline: 'Tabla înmulțirii, împărțirea și probleme.',
  intro: 'Al treilea volum face tabla înmulțirii vizuală și ușor de reținut și duce elevii până la împărțirea cu rest și ordinea operațiilor.',
  items: ['Tabla înmulțirii, vizual', 'Înmulțirea în scris', 'Împărțirea cu și fără rest', 'Ordinea efectuării operațiilor', 'Probleme pe etape', 'Jocuri de calcul mintal', 'Fișe pe 3 niveluri', 'Fișe cu răspunsuri', 'Teste și bareme'],
  outro: 'Tabla înmulțirii, învățată cu plăcere.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volumul 04',
  level: 'Cl. II–VIII',
  title: 'Fracții, zecimale și procente',
  tagline: 'De la jumătate la procente, explicat cu desene.',
  intro: 'Al patrulea volum explică fracțiile cu discuri, bare și axa numerelor, apoi trece la numere zecimale și procente.',
  items: ['Jumătate, sfert, treime', 'Fracții echivalente', 'Compararea fracțiilor', 'Operații cu fracții', 'Numere zecimale', 'Procente din viața reală', 'Rapoarte și proporții', 'Fișe cu răspunsuri', 'Teste scurte'],
  outro: 'Fracțiile nu mai sunt o sperietoare.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volumul 05',
  level: 'Cl. I–VIII',
  title: 'Geometrie și măsurări',
  tagline: 'Figuri, corpuri, unități de măsură și date.',
  intro: 'Al cincilea volum aduce geometria în mâinile elevilor: figuri, corpuri, perimetre, arii, volume, unități de măsură și grafice.',
  items: ['Figuri și corpuri geometrice', 'Perimetrul și aria', 'Unghiuri și triunghiuri', 'Unități de măsură și transformări', 'Volumul corpurilor', 'Tabele și grafice', 'Desen cu rigla, echerul și compasul', 'Fișe cu răspunsuri', 'Teste și bareme'],
  outro: 'Elevii văd și ating ce învață.',
  spine: 'bg-purple-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volumul 06',
  level: 'Cl. V–VIII',
  title: 'Algebră și Evaluarea Națională',
  tagline: 'Ecuații, funcții și pregătire pentru clasa a VIII-a.',
  intro: 'Al șaselea volum acoperă ecuațiile, inecuațiile și funcțiile și conține modele de subiecte pentru pregătirea Evaluării Naționale.',
  items: ['Ecuații și inecuații', 'Calcul algebric', 'Funcții și grafice', 'Teorema lui Pitagora', 'Modele de subiecte pentru EN', 'Calificative FB, B, S, I la primar', 'Note de la 1 la 10 la gimnaziu', 'Teste în variantele A și B', 'Bareme și răspunsuri'],
  outro: 'Pregătirea pentru examen devine un plan clar.',
  note: 'Materialul este creat independent, după programa școlară de matematică în vigoare, și nu este o publicație a Ministerului Educației.',
  spine: 'bg-teal-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 volume digitale pentru clasele I – a VIII-a',
  text: 'Un pachet complet de matematică care te ajută să planifici și să predai tot anul școlar. Fiecare volum are aproximativ 40 de pagini cu lecții, fișe de lucru, jocuri, teste și răspunsuri.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lecții gata de predat',
  text: 'Nu mai pornești de la o pagină goală. Lecții clare, minut cu minut, de la calculul mintal la feedbackul de la final.'
}, {
  icon: FileTextIcon,
  title: 'Fișe de lucru printabile',
  text: 'Fișe pe care le printezi și le folosești imediat – exerciții gradate, de la simplu la provocare.'
}, {
  icon: ShapesIcon,
  title: 'Matematică vizuală',
  text: 'Blocuri în baza 10, axa numerelor, tabele și desene mari pe fiecare pagină.'
}, {
  icon: DicesIcon,
  title: 'Jocuri și activități',
  text: 'Jocuri de 5–10 minute pentru calculul mintal și pentru recapitulare, care îi țin pe elevi implicați.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Evaluări și răspunsuri',
  text: 'Teste cu punctaj, descriptori de performanță și răspunsuri verificate pentru corectare rapidă.'
}, {
  icon: CalendarCheckIcon,
  title: 'Organizat pe clase',
  text: 'Fiecare lecție este marcată cu clasa, de la clasa I la a VIII-a.'
}];
export const pageSamples: Array<{src: string; label: string; title: string; text: string}> = [
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/3724e2ee-d2eb-4487-b051-d7663ac07b3d/1793844264831_vol1-p10-ae4cb242.webp",
    "label": "Lecția 1 · Pagina 10",
    "title": "Numerele de la 0 la 20",
    "text": "Cadrul de 10, un exemplu rezolvat, exerciții la tablă cu răspunsuri și momentele orei minut cu minut."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/3724e2ee-d2eb-4487-b051-d7663ac07b3d/1793844270295_vol1-p14-e629a814.webp",
    "label": "Fișa 3 · Pagina 14",
    "title": "Bare și cubulețe",
    "text": "O fișă printabilă cu blocuri în baza 10: elevii scriu numărul și îl descompun în zeci și unități."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/3724e2ee-d2eb-4487-b051-d7663ac07b3d/1793844276104_vol1-p24-fcd3538d.webp",
    "label": "Fișa 9 · Pagina 24",
    "title": "Cifrele romane",
    "text": "Transformări în ambele sensuri, un ceas cu cifre romane și secolul în care trăim."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/3724e2ee-d2eb-4487-b051-d7663ac07b3d/1793844282427_vol1-p32-ee6c42d3.webp",
    "label": "Fișa 14 · Pagina 32",
    "title": "Detectivul de multipli",
    "text": "Tabelul numerelor de la 1 la 100: elevii colorează multiplii lui 3 și descoperă regula lui 9."
  },
  {
    "src": "https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/3724e2ee-d2eb-4487-b051-d7663ac07b3d/1793844288563_vol1-p33-d1ce5aee.webp",
    "label": "Lecția 10 · Pagina 33",
    "title": "Numere prime și factori primi",
    "text": "Arborele de factori pas cu pas, pentru clasa a VI-a, cu exercițiile rezolvate pentru profesor."
  }
];
export const pageTypes = ['Lecții gata de predat', 'Fișe printabile', 'Jocuri matematice', 'Evaluări și răspunsuri'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Banca de teste',
  text: 'Teste scurte și lucrări de control pentru clasele I–VIII, în variantele A și B.',
  items: ['Variantele A și B', 'Punctaj pe fiecare item', 'Barem de corectare', 'Toate răspunsurile'],
  benefit: 'Testul săptămânii, gata în câteva secunde.'
}, {
  title: 'Atlasul formulelor și planșe de perete',
  text: 'Planșe colorate de printat pentru clasă și fișe-memento pentru elevi.',
  items: ['Tabla înmulțirii', 'Unități de măsură', 'Perimetre și arii', 'Formule pentru gimnaziu'],
  benefit: 'O clasă care predă și când tu taci.'
}, {
  title: 'Comunicarea cu părinții',
  text: '40 de mesaje gata scrise pentru catalogul electronic, e-mail și ședințe.',
  items: ['Laude și progrese', 'Rămâneri în urmă', 'Teme și lucrări de control', 'Idei de exersare acasă'],
  benefit: 'Comunicare clară, fără să scrii totul de la zero.'
}, {
  title: 'Planificatorul profesorului 2026–2027',
  text: 'Calendarul anului școlar, planificare pe 36 de săptămâni și pregătirea pentru Evaluarea Națională.',
  items: ['Calendarul pe module', 'Planificare săptămânală', 'Catalog de note', 'Calendarul pregătirii EN'],
  benefit: 'Tot anul, organizat pe o singură hârtie.'
}, {
  title: 'Biblioteca de jocuri matematice',
  text: '30 de jocuri fără pregătire pentru calcul mintal, tabla înmulțirii, fracții și geometrie.',
  items: ['Reguli pas cu pas', 'Variante pe 3 niveluri', 'Cartonașe de decupat', 'Răspunsuri'],
  benefit: 'Elevii cer singuri „încă o rundă”.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Deschizi MATEMATICĂ GATA DE PREDAT™',
  text: 'Descarci pachetul și deschizi volumul potrivit pentru tema ta.'
}, {
  title: 'Alegi lecția și fișa',
  text: 'Găsești tema și alegi lecția, fișa sau jocul potrivit pentru clasa ta.'
}, {
  title: 'Te pregătești în 5 minute',
  text: 'Citești planul minut cu minut și printezi ce ai nevoie.'
}, {
  title: 'Printezi și predai',
  text: 'Printezi fișele sau folosești materialul digital – și intri la oră cu un plan clar.'
}];
export const audienceItems = ['Predai matematică la clasele I–IV', 'Predai matematică la gimnaziu, clasele V–VIII', 'Vrei tot materialul anului școlar organizat', 'Nu vrei să mai faci fiecare fișă de la zero', 'Ai nevoie de activități care îi țin pe copii atenți', 'Vrei explicații vizuale, cu desene și tabele', 'Vrei teste, bareme și răspunsuri gata făcute', 'Vrei să câștigi timp la planificarea săptămânală'];
export const timeValueTasks = ['Cauți fișe și exerciții', 'Scrii probleme și calculezi răspunsurile', 'Desenezi axe, tabele și figuri', 'Pregătești testele și baremele', 'Corectezi și urmărești progresul elevilor'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 volume digitale – 240 de pagini',
  detail: 'Numere, adunare și scădere, înmulțire și împărțire, fracții, geometrie, algebră'
}, {
  label: '5 bonusuri fără cost suplimentar',
  detail: 'Banca de teste, atlasul formulelor, părinți, planificator și jocuri matematice'
}, {
  label: 'Pentru clasele I – a VIII-a',
  detail: 'Lecții gata de predat, fișe printabile și răspunsuri'
}];
export const offerIncludes = ['Descărcare digitală imediată', 'Fișiere PDF de păstrat', 'Fișe printabile', 'Nu se trimite produs fizic'];
export const PRICE = '99 lei';

// Only add authentic reviews. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Cum primesc materialul?',
  a: 'MATEMATICĂ GATA DE PREDAT™ se livrează digital. După cumpărare primești acces la fișiere PDF pe care le poți descărca și păstra. Le poți folosi pe calculator, tabletă sau telefon și printezi fișele de care ai nevoie.'
}, {
  q: 'Este adaptat pentru școala din România?',
  a: 'Da. Materialul este creat pentru ora de matematică, clasele I – a VIII-a, după structura programei școlare în vigoare (la clasele I–II, pentru „Matematică și explorarea mediului”). Este un material independent, nu o publicație a Ministerului Educației.'
}, {
  q: 'Este potrivit pentru clasele I – a VIII-a?',
  a: 'Da. Cele șase volume au materiale pentru primar și pentru gimnaziu. Fiecare lecție este marcată cu clasa, ca să alegi repede ce se potrivește.'
}, {
  q: 'Chiar pot pregăti o oră în 5 minute?',
  a: 'MATEMATICĂ GATA DE PREDAT™ este gândit ca să-ți scurteze pregătirea. Deschizi un volum, alegi o lecție gata făcută și o parcurgi în aproximativ cinci minute. Timpul poate varia în funcție de lecție, de clasă și de adaptările de care ai nevoie.'
}, {
  q: 'Ajunge materialul pentru tot anul școlar?',
  a: 'Da. Pachetul conține lecții și activități pentru tot anul, organizate pe domeniile programei. Le alegi, le combini și le adaptezi după planificarea ta.'
}, {
  q: 'Răspunsurile sunt verificate?',
  a: 'Da. Fiecare volum include răspunsurile la fișe și baremele testelor, calculate și verificate exercițiu cu exercițiu.'
}, {
  q: 'Ajută și la pregătirea Evaluării Naționale?',
  a: 'Da. Volumul 6 conține modele de subiecte și recapitulări pentru clasa a VIII-a, iar celelalte volume acoperă bazele de care elevii au nevoie la examen.'
}, {
  q: 'Pot folosi materialul cu elevi de niveluri diferite?',
  a: 'Da. Exercițiile sunt gradate, de la simplu la provocare, iar multe activități se pot adapta ușor pentru alte clase.'
}, {
  q: 'Trebuie să printez tot?',
  a: 'Nu. Alegi singur ce pagini printezi și poți folosi o parte din material și digital, pe tablă sau pe videoproiector.'
}, {
  q: 'Sunt incluse cele cinci bonusuri?',
  a: `Da. Toate cele cinci bonusuri sunt incluse în pachet fără cost suplimentar – totul pentru ${PRICE}.`
}, {
  q: 'Este un abonament?',
  a: 'Nu. Plătești o singură dată și primești acces la materialul digital. Fără plăți recurente.'
}, {
  q: 'Pot păstra fișierele?',
  a: 'Da. Poți descărca și păstra fișierele PDF pentru folosire ulterioară, conform condițiilor de utilizare ale produsului.'
}, {
  q: 'Pot folosi materialul pe telefon sau tabletă?',
  a: 'Da. Fișierele PDF se deschid pe calculator, tabletă și telefon. Pentru a verifica așezarea fișelor înainte de printare, îți recomandăm calculatorul sau tableta.'
}, {
  q: 'Există garanție?',
  a: 'Da. MATEMATICĂ GATA DE PREDAT™ are garanție de satisfacție de 7 zile. Dacă materialul nu corespunde așteptărilor tale, ne poți contacta și cere rambursarea în perioada de garanție, conform condițiilor.'
}];
export const footerLinks = ['Termeni și condiții', 'Politica de confidențialitate', 'Politica de rambursare', 'Contact'];
